import ValidateModule from "./ValidateModule";

import type {IEventChannel, ApiOpts, SfProps} from "../model/Models";
import type AjaxRequestBroker from "../AjaxRequestBroker";
import type CacheModule from "./CacheModule";

const $: JQueryStatic = global.$;
const es = global.es;

/**
 * @class TerritoryModule
 */
export default class TerritoryModule {
    private eventChannel: IEventChannel;
    private rqBroker: AjaxRequestBroker;
    private resourceName: string;
    private cache: CacheModule;

    /**
     * 
     * @param eventChannel 
     * @param rqBroker 
     * @param resourceName 
     * @param cache 
     */
    constructor(eventChannel: IEventChannel, rqBroker: AjaxRequestBroker, resourceName: string, cache: CacheModule) {
        this.eventChannel = eventChannel;
        this.rqBroker = rqBroker;
        this.resourceName = resourceName;
        this.cache = cache;

        this.getBaseLayersConfig = this.getBaseLayersConfig.bind(this);
        this.getTerritoryMeta = this.getTerritoryMeta.bind(this);
        this.updateLevelLabels = this.updateLevelLabels.bind(this);
        this.getTerritoryMetaByGeoMapId = this.getTerritoryMetaByGeoMapId.bind(this);
        this.getRelatedLayerViewMapping = this.getRelatedLayerViewMapping.bind(this);
        this.create = this.create.bind(this);
        this.saveAll = this.saveAll.bind(this);
        this.loadAll = this.loadAll.bind(this);
        this.loadFromJson = this.loadFromJson.bind(this);
        this.loadAllByGeoMapId = this.loadAllByGeoMapId.bind(this);
        this.loadWorkIndexData = this.loadWorkIndexData.bind(this);
        this.calculateWorkIndex = this.calculateWorkIndex.bind(this);
        this.editTerritoryDerivedAttributes = this.editTerritoryDerivedAttributes.bind(this);
        this.editTerritoryAttributes = this.editTerritoryAttributes.bind(this);
        this.downloadValidatedFile = this.downloadValidatedFile.bind(this);
        this.deleteValidatedFile = this.deleteValidatedFile.bind(this);
        this.importTerritoryFromLayer2 = this.importTerritoryFromLayer2.bind(this);
        this.importTerritoryFromLayer = this.importTerritoryFromLayer.bind(this);
        this.addTerritoryExceptions2 = this.addTerritoryExceptions2.bind(this);
        this.addTerritoryExceptions = this.addTerritoryExceptions.bind(this);
        this.exportTerritoryLayerMeta = this.exportTerritoryLayerMeta.bind(this);
        this.exportTerritoryGeoMap = this.exportTerritoryGeoMap.bind(this);
        this.driveTime = this.driveTime.bind(this);
        this.radius = this.radius.bind(this);
        this.deleteLinkedAttributesByGeoMapId = this.deleteLinkedAttributesByGeoMapId.bind(this);
        this.optimize = this.optimize.bind(this);
        this.optimizeNoPoll = this.optimizeNoPoll.bind(this);
        this.isOptimizeDriveTimeEnabled = this.isOptimizeDriveTimeEnabled.bind(this);
        this.getUsedPolygons = this.getUsedPolygons.bind(this);
        this.getTerritoryLimits = this.getTerritoryLimits.bind(this);
        this.hasPbf = this.hasPbf.bind(this);
        this.createCentersDataset = this.createCentersDataset.bind(this);
        this.fillGaps = this.fillGaps.bind(this);
        this.deriveExceptionsFromSfAccounts = this.deriveExceptionsFromSfAccounts.bind(this);
        this.getSFExtraFieldsSummary = this.getSFExtraFieldsSummary.bind(this);
        this.getLoadValues = this.getLoadValues.bind(this);
        this.createFromDriveTime = this.createFromDriveTime.bind(this);
    }

    /**
     * Gets default base layers configuration
     * 
     * @param layerMetaId 
     * @param opts 
     * 
     * @returns 
     */
    getBaseLayersConfig(layerMetaId?: number, opts?: ApiOpts) {
        // layerMetaId is optional, so if only one arg and it is object, it is opts...
        if (typeof layerMetaId == 'object' && !opts) {
            opts = layerMetaId;
            layerMetaId = null;
        }

        let url = es.api.getURL(this.resourceName) + '/base_layers_config';
        if (layerMetaId) {
            if (!ValidateModule.checkId(layerMetaId)) {
                return es.util.deferred.rejected();
            }

            url += '/' + layerMetaId;
        }

        return this.cache.doGet(this.rqBroker, this.resourceName + '_blc_' + layerMetaId, url, 'cache_key', opts);
    }

    /**
     * Gets territory meta
     * e.g. {"baseLayerMetaId": 211, "created": 1553183295387, "id": 206, "lastModified": 1553183295387, "territoryDerivedAttributes": []}
     * 
     * @param territoryMetaId 
     * @param opts 
     * 
     * @returns 
     */
    getTerritoryMeta(territoryMetaId: number, opts?: ApiOpts) {
        if (!ValidateModule.checkId(territoryMetaId)) {
            return es.util.deferred.rejected();
        }

        const url = es.api.getURL(this.resourceName) + '/meta/' + territoryMetaId;
        return this.cache.doGet(this.rqBroker, this.resourceName + '_meta', url, territoryMetaId, opts);
    }

    /**
     * 
     * @param territoryMetaId 
     * @param levelLabels 
     * @param opts 
     * 
     * @returns 
     */
    updateLevelLabels(territoryMetaId: number, levelLabels: string[], opts?: ApiOpts) {
        if (!ValidateModule.checkId(territoryMetaId)) {
            return es.util.deferred.rejected();
        }

        if (!ValidateModule.checkMandatoryArray(levelLabels)) {
            return es.util.deferred.rejected();
        }

        const url = es.api.getURL(this.resourceName) + '/meta/' + territoryMetaId + '/level_labels';

        return this.rqBroker.doPut({
            'url': url,
            'opts': opts,
            'data': levelLabels
        }).done((territoryMeta) => {
            this.cache.setEntity(this.resourceName + '_meta', territoryMeta);
            this.eventChannel.fireEvent("onEditTerritoryLevelLabels", territoryMeta);
        });
    }

    /**
     * Gets territory meta by geoMapId
     * Convenience method so it can be fetched concurrently with geoMap entity and will not be blocked (cache/preload notwithstanding; calls would still need to be linear)
     * 
     * @param geoMapId 
     * @param opts 
     * 
     * @returns 
     */
    getTerritoryMetaByGeoMapId(geoMapId: number, opts?: ApiOpts) {
        if (!ValidateModule.checkId(geoMapId)) {
            return es.util.deferred.rejected();
        }

        const url = es.api.getURL(this.resourceName) + '/meta/geomap/' + geoMapId;
        return this.cache.doGet(this.rqBroker, this.resourceName + '_meta_geomap', url, geoMapId, opts);
    }

    /**
     * 
     * @param geoMapId 
     * @param opts 
     * 
     * @returns 
     */
    getRelatedLayerViewMapping(geoMapId: number, opts: ApiOpts) {
        if (!ValidateModule.checkId(geoMapId)) {
            return es.util.deferred.rejected();
        }

        const url = es.api.getURL(this.resourceName) + '/related_layerview_mapping/' + geoMapId;
        return this.cache.doGet(this.rqBroker, this.resourceName + '_related_layerview_mapping', url, geoMapId, opts);
    };
    
    importABFile(filename: string, model: object, opts: ApiOpts) {
        const data = {
            'key': filename,
            'meta': model
        };
        
        opts = opts || {};
        opts.resolveWithXHR = true;
        
        const url = es.api.getURL(this.resourceName) + '/import/ab_from_file';
        return this.rqBroker.doPost({
            url: url,
            data: data,
            opts: opts
        }).then((rs, jqXHR) => {
            const geoMapId = parseInt(jqXHR.getResponseHeader("es_geomap_id"));
            return es.api.geoMap.get(geoMapId).then((geoMap) => es.util.deferred.resolved(geoMap, rs));
        });
    }

    /**
     * Create a new TerritoryGeoMap
     * 
     * @param baseLayerMetaId 
     * @param levels 
     * @param title 
     * @param type 
     * @param basePointLayerMetaId 
     * @param sfProps 
     * @param opts 
     * 
     * @returns 
     */
    create(baseLayerMetaId?: number, levels?: number, title?: string, type: string, basePointLayerMetaId?: number, sfProps?: SfProps, opts?: ApiOpts) {
        let url = es.api.getURL(this.resourceName) + "?type=" + type;

        if (levels) {
            url += "&levels=" + levels;
        }
        if (title) {
            url += "&title=" + es.util.url.stringify(title);
        }
        if (baseLayerMetaId) {
            url += "&baseLayerMetaId=" + baseLayerMetaId;
        }
        if (basePointLayerMetaId) {
            url += "&basePointLayerMetaId=" + basePointLayerMetaId;
        }

        return this.rqBroker.doPost({
            url: url,
            opts: opts,
            data: sfProps
        });
    }

    /**
     * Save all territoryFeatures
     * note: do not call this when in the middle of opt or gap filling
     * 
     * @param territoryMainLayerViewId 
     * @param territoryFeatures 
     * @param opts 
     * 
     * @returns 
     */
    saveAll(territoryMainLayerViewId: number, territoryFeatures, opts?: ApiOpts) {
        return getAttrTypeMap(territoryMainLayerViewId).then((attrTypeMap) => {
            // Cleanup and validation
            for (const terFeat of territoryFeatures) {
                processFeatureBeforeSave(terFeat, attrTypeMap);

                if (!terFeat['NAME']) {
                    const errMsg = "Territories must have NAME attribute: " + JSON.stringify(terFeat);
                    es.log.error(errMsg);
                    //es.error.handleJSError(errMsg);
                    // the only sensible thing to do here really is auto-fix the issue by giving the ter a name. the user isn't going to want it in a broken state.
                    terFeat.NAME = "Unknown_" + terFeat.ES_ID;
                }
            }
        }).then(() => {
            processFeaturesOrderBeforeSave(territoryFeatures);
        }).then(() => {
            es.log.log("saving territories", territoryFeatures);
            const url = es.api.getURL(this.resourceName) + "/save/" + territoryMainLayerViewId;

            return this.rqBroker.doPost({
                'url': url,
                'opts': opts,
                'data': territoryFeatures
            });
        }).then((rs) => {
            this.cache.clear(this.resourceName);
            this.cache.clear("LayerInfo");
            this.cache.clear("LayerView");
            this.cache.clear("LayerInfo_LayerView");

            return Promise.all([
                es.api.layerView.get(territoryMainLayerViewId),
                es.api.layerInfo.getForLayerView(territoryMainLayerViewId)
            ]).then(([mainLayerView, mainLayerInfo]) => {
                return es.api.geoMap.get(mainLayerView.geoMapId).then((geoMap) => {
                    this.eventChannel.fireEvent("onUpdateLayerView", mainLayerView, mainLayerInfo, geoMap.lastModified, opts);

                    return rs; // only resolve after all is updated as map listen for onUpdate events, this way we ensure when it-s resolved map is updated
                });
            });
        });
    }

    /**
     * Load territoryFeatures
     * [{"ES_GEOM_STATUS_CODE":"TO_BE_SET","ES_STYLE_COLOR":234242,"ES_BASE_FEATURE_IDS":[2,3,4,5],"NAME":"Territory 1","ES_ID":1},{"ES_GEOM_STATUS_CODE":"TO_BE_SET","ES_STYLE_COLOR":345426,"ES_BASE_FEATURE_IDS":[22,23,35],"NAME":"Territory 2","ES_ID":2}]
     * 
     * @param territoryMainLayerViewId 
     * @param opts 
     * 
     * @returns 
     */
    loadAll(territoryMainLayerViewId: number, opts?: ApiOpts) {
        const url = es.api.getURL(this.resourceName) + "/load/" + territoryMainLayerViewId;
        return $.when(
            this.cache.doGet(this.rqBroker, this.resourceName + '_loadAll', url, territoryMainLayerViewId, opts),
            getAttrTypeMap(territoryMainLayerViewId)
        ).then((features, attrTypeMap) => {
            features.forEach((f) => processFeatureAfterLoad(f, attrTypeMap));

            return features;
        });
    }

    /**
     * 
     * @param territoryMainLayerViewId 
     * @param territoryFeatures 
     * 
     * @returns 
     */
    loadFromJson(territoryMainLayerViewId: number, territoryFeatures: any[]) {
        return getAttrTypeMap(territoryMainLayerViewId).then((attrTypeMap)=> {
            territoryFeatures.forEach((f) => processFeatureAfterLoad(f, attrTypeMap));

            return territoryFeatures;
        });
    }

    /**
     * Load territoryFeatures by geoMapId
     * [{"ES_GEOM_STATUS_CODE":"TO_BE_SET","ES_STYLE_COLOR":234242,"ES_BASE_FEATURE_IDS":[2,3,4,5],"NAME":"Territory 1","ES_ID":1},{"ES_GEOM_STATUS_CODE":"TO_BE_SET","ES_STYLE_COLOR":345426,"ES_BASE_FEATURE_IDS":[22,23,35],"NAME":"Territory 2","ES_ID":2}]
     * 
     * @param geoMapId 
     * @param opts 
     * 
     * @returns 
     */
    loadAllByGeoMapId(geoMapId: number, opts?: ApiOpts) {
        return es.api.layerView.list(es.util.criteria.and({
            '$type': 'Equal',
            'attr': 'geoMapId',
            'value': geoMapId
        }, {
            '$type': 'Equal',
            'attr': '$type',
            'value': 'ManageTerritoryMainLayerView'
        })).then((lvs) => {
            if (lvs.length === 0) {
                return es.util.deferred.rejected('No ManageTerritoryMainLayerView in geoMap ' + geoMapId);
            }

            if (lvs.length > 1) {
                return es.util.deferred.rejected('Found more than one ManageTerritoryMainLayerView in geoMap ' + geoMapId);
            }

            const territoryMainLayerViewId = lvs[0].id;

            return this.loadAll(territoryMainLayerViewId, opts);
        });
    }
    
    /**
     * 
     * @param territoryMainLayerViewId 
     * @param opts 
     * 
     * @returns 
     */
    loadWorkIndexData(territoryMainLayerViewId: number, opts?: ApiOpts) {
        const url = es.api.getURL(this.resourceName) + '/territory_derived_attributes/work_index_data/' + territoryMainLayerViewId;

        return this.cache.doGet(this.rqBroker, this.resourceName + '_loadWorkIndexData', url, territoryMainLayerViewId, opts);
    }

    /**
     * 
     * @param territoryMainLayerViewId 
     * @param opts 
     * 
     * @returns 
     */
    calculateWorkIndex(territoryMainLayerViewId: number, opts?: ApiOpts) {
        let url = es.api.getURL(this.resourceName) + '/territory_derived_attributes/calculate_work_index/' + territoryMainLayerViewId;
        return this.rqBroker.doGet({
            url: url,
            opts: opts
        });
    }

    /**
     * 
     * @param resourceObj 
     * @param mainId 
     * @param opts 
     * 
     * @returns 
     */
    editTerritoryDerivedAttributes(resourceObj: object, mainId: number, opts?: ApiOpts) {
        const updateLayerViews = (toReloadLayerViews: number[], geoMapLastModified: number) => {
            return Promise.all(toReloadLayerViews.flatMap((layerViewId) => {
                return [es.api.layerView.get(layerViewId), es.api.layerInfo.getForLayerView(layerViewId)];
            })).then((rs) => {
                for (let i=0; i<rs.length; i+= 2) {
                    const layerView = rs[i];
                    const layerInfo = rs[i+1];
                    
                    this.eventChannel?.fireEvent("onUpdateLayerView", layerView, layerInfo, geoMapLastModified);
                }
                
                return Promise.resolve();
            });
        }

        if (!ValidateModule.checkMandatoryArg(resourceObj) ||
            !ValidateModule.checkId(mainId)) {
            return es.util.deferred.rejected();
        }

        opts = opts || {};
        opts.resolveWithXHR = true;
        return this.rqBroker.doPost({
            'url': es.api.getURL(this.resourceName)+ "/territory_derived_attributes/edit/" + mainId,
            'opts': opts,
            'data': resourceObj
        }).then((rs, jqXHR) => {
            this.cache.clear(this.resourceName);
            this.cache.clear("LayerInfo");
            this.cache.clear("LayerView");
            this.cache.clear("LayerInfo_LayerView");
            this.cache.clear("Thematic_COLOR");
            this.cache.clear("Thematic_SIZE");

            const geoMapLastModified = parseInt(jqXHR.getResponseHeader("es_geomap_last_modified"));
            const toReloadLayerViews = JSON.parse(jqXHR.getResponseHeader("es_layer_views_modified"));
            const taskId = jqXHR.getResponseHeader("es_task_id");

            return $.when(updateLayerViews(toReloadLayerViews, geoMapLastModified)).then(() => {
                if (!taskId) { // if there's a task, it will be fired when task finished on Status module
                    this.eventChannel.fireEvent("onEditTerritoryDerivedAttribute", rs);
                }
                
                return taskId ? es.util.deferred.resolved(rs, taskId) : es.util.deferred.resolved(rs);
            });
        });
    }

    /**
     * 
     * @param resourceObj 
     * @param id 
     * @param opts 
     * 
     * @returns 
     */
    editTerritoryAttributes(resourceObj: object, id: number, opts?: ApiOpts) {
        if (!ValidateModule.checkMandatoryArg(resourceObj) ||
            !ValidateModule.checkId(id)) {
            return es.util.deferred.rejected();
        }

        opts = opts || {};
        opts.resolveWithXHR = true;
        const url = es.api.getURL(this.resourceName)+ "/territory_attributes/edit/" + id;

        return this.rqBroker.doPost({
            'url': url,
            'opts': opts,
            'data': resourceObj
        }).done((rs, jqXHR) => {
            this.cache.clear(this.resourceName);
            this.cache.clear("LayerInfo");
            this.cache.clear("LayerView");
            this.cache.clear("LayerInfo_LayerView");
            this.cache.clear("Thematic_COLOR");
            this.cache.clear("Thematic_SIZE");

            const geoMapLastModified = parseInt(jqXHR.getResponseHeader("es_geomap_last_modified"));

            Promise.all([
                es.api.layerView.get(id),
                es.api.layerInfo.getForLayerView(id)
            ]).then(([layerView, layerInfo]) => {
                this.eventChannel.fireEvent("onUpdateLayerView", layerView, layerInfo, geoMapLastModified);
                this.eventChannel.fireEvent("onEditTerritoryAttribute", "CHANGE_COLUMNS", rs);
            });
        });
    }

    /**
     * 
     * @param filename 
     * 
     * @returns 
     */
    downloadValidatedFile(filename: string) {
        if (!ValidateModule.checkMandatoryArg(filename)) {
            return es.util.deferred.rejected();
        }

        const downloadUrl = es.api.getURL(this.resourceName)+ "/import/download_validated/" + es.util.url.stringify(filename);
        return $.fileDownload(downloadUrl);
    }

    /**
     * 
     * @param filename 
     * @param opts 
     * 
     * @returns 
     */
    deleteValidatedFile(filename: string, opts?: ApiOpts) {
        if (!ValidateModule.checkMandatoryArg(filename)) {
            return es.util.deferred.rejected();
        }

        const url = es.api.getURL(this.resourceName)+ "/import/delete_validated/" + es.util.url.stringify(filename);
        return this.rqBroker.doDelete({'url': url, 'opts': opts});
    }

    /**
     * 
     * @param importLayerMetaId 
     * @param territoryGeoMapId 
     * @param levelAttributeNames 
     * @param extraAttributeNames 
     * 
     * @returns 
     */
    importTerritoryFromLayer2(importLayerMetaId: number, territoryGeoMapId: number, levelAttributeNames: string[], extraAttributeNames: string[]) {
        const territoryImportInfo = {
            'importLayerMetaId' : importLayerMetaId,
            'territoryGeoMapId' : territoryGeoMapId,
            'levelAttributeNames' : levelAttributeNames,
            'extraAttributeNames' : extraAttributeNames
        }

        return this.importTerritoryFromLayer(territoryImportInfo);
    }

    /**
     * Import territory from existing layer meta.
     * Import accounts only.
     *
     * @param territoryImportFromLayerInfo
     * @param opts
     * 
     * @returns
     */
    importTerritoryFromLayer(territoryImportFromLayerInfo: object, opts?: ApiOpts) {
        const url = es.api.getURL(this.resourceName) + "/import/from_layer";

        return this.rqBroker.doPost({
            'url' : url,
            'opts' : opts,
            'data' : territoryImportFromLayerInfo
        });
    }

    /**
     * 
     * @param exceptionsLayerMetaId 
     * @param territoryGeoMapId 
     * 
     * @returns 
     */
    addTerritoryExceptions2(exceptionsLayerMetaId: number, territoryGeoMapId: number) {
        const territoryAddExceptionsInfo = {
            'exceptionsLayerMetaId' : exceptionsLayerMetaId,
            'territoryGeoMapId' : territoryGeoMapId
        };

        return this.addTerritoryExceptions(territoryAddExceptionsInfo)
    }

    /**
     * 
     * @param territoryAddExceptionInfo 
     * @param opts 
     * 
     * @returns 
     */
    addTerritoryExceptions(territoryAddExceptionInfo: object, opts?: ApiOpts) {
        return this.rqBroker.doPost({
            'url' : es.api.getURL(this.resourceName) + "/import/add_exceptions",
            'opts' : opts,
            'data' : territoryAddExceptionInfo
        });
    }

    /**
     * Export by territory layer meta id.
     *
     * @param territoryLayerMetaId
     * 
     * @returns opens new window with exported file
     */
    exportTerritoryLayerMeta(territoryLayerMetaId: number) {
        if (!ValidateModule.checkId(territoryLayerMetaId)){
            return es.util.deferred.rejected();
        }

        const downloadUrl = es.api.getURL(this.resourceName) + "/export/layer_meta/" + territoryLayerMetaId;
        return $.fileDownload(downloadUrl);
    }

    /**
     * Export by territory geo map id. Could be used both territory temp geomap id or territory geomap id.
     *
     * @param geoMapId territory temp geomap id or territory geomap id
     * 
     * @returns opens new window with exported file
     */
    exportTerritoryGeoMap(geoMapId: number) {
        if (!ValidateModule.checkId(geoMapId)){
            return es.util.deferred.rejected();
        }

        const downloadUrl = es.api.getURL(this.resourceName) + "/export/geo_map/" + geoMapId;
        return $.fileDownload(downloadUrl);
    }

    /**
     * Creates territory features based on drive-times from specified params
     * 
     * @param meta 
     * @param opts 
     * 
     * @returns 
     */
    driveTime(meta: object, opts?: ApiOpts) {
        if (!ValidateModule.checkMandatoryArg(meta)) {
            return es.util.deferred.rejected();
        }

        const url = es.api.getURL(this.resourceName) + "/drive_time";

        return this.rqBroker.doGet({
            // 'url': es.util.url.fromParams(meta, es.api.getURL(resourceName) + "/drive_time?"),
            url: url, 
            data: meta,
            'opts': opts,
        });
    }

    /**
     * Creates territory features based on radius from specified params
     * 
     * @param meta 
     * @param opts 
     * 
     * @returns 
     */
    radius(meta: object, opts?: ApiOpts) {
        if (!ValidateModule.checkMandatoryArg(meta)) {
            return es.util.deferred.rejected();
        }

        return this.rqBroker.doGet({
            // 'url': es.util.url.fromParams(meta, es.api.getURL(resourceName) + "/radius?") ,
            url: es.api.getURL(this.resourceName) + "/radius",
            data: meta,
            // 'opts': meta,
        });
    }

    /**
     * 
     * @param geoMapId 
     * @param opts 
     * 
     * @returns 
     */
    deleteLinkedAttributesByGeoMapId(geoMapId: number, opts?: ApiOpts) {
        const url = es.api.getURL(this.resourceName) + "/delete_linked_attributes/" + geoMapId;

        return this.rqBroker.doDelete({'url': url, 'opts': opts});
    }

    /**
     * optimize territory
     *
     * @param meta = {
     *                    manageTerritoryMainLayerViewId: long e.g. 178,
     *                    numTerritories: int e.g. 10,
     *                    derivedAttribute: optional,
     *                    pointsLayerViewId: long e.g. 181,
     *                    useRoadNetwork: boolean optional,
     *                    centersLayerViewId: long optional
     *                }
     * @param opts
     * 
     * @returns a Promise
     */
    optimize(meta: object, opts?: ApiOpts) {
        if (!ValidateModule.checkMandatoryArg(meta)) {
            return es.util.deferred.rejected();
        }

        const url = es.api.getURL(this.resourceName) + "/optimize";

        return this.rqBroker.doPost({
            'url': url,
            'opts': opts,
            'data': meta
        });
    }

    /**
     * Call optimize but adds a wrapper around the poll part and resolves when optimize has completed.
     * 
     * @param meta 
     * @param statusCallbackFn 
     * @param opts 
     * 
     * @returns 
     */
    optimizeNoPoll(meta: object, statusCallbackFn: Function, opts?: ApiOpts) {
        const dfd = $.Deferred();
        es.api.territory.optimize(meta, opts).done((optimizeRs) => {
            const checkProgress = () => {
                es.api.status.getStatus(optimizeRs.taskId).done((statusRs) => {
                    if (statusCallbackFn) {
                        statusCallbackFn(statusRs);
                    }

                    if (statusRs.percentage === 100) {
                        dfd.resolve(statusRs);
                    } else {
                        window.setTimeout(checkProgress, 1000); // check progress again in a second
                    }
                }).fail((rs) => {
                    dfd.reject("Error polling optimize", rs);
                });
            };

            checkProgress();
        });

        return dfd.promise();
    }

    /**
     * 
     * @param baseLayerMetaId 
     * @param opts 
     * 
     * @returns 
     */
    isOptimizeDriveTimeEnabled(baseLayerMetaId: number, opts?: ApiOpts) {
        if (!ValidateModule.checkId(baseLayerMetaId)) {
            return es.util.deferred.rejected();
        }

        const url = es.api.getURL(this.resourceName) + "/optimize/drive_time_enabled";

        return this.rqBroker.doGet({
            'url': url,
            'opts': opts
        }).then((driveTimeEnabledLayerMetasId) => {
            return driveTimeEnabledLayerMetasId.includes(baseLayerMetaId);
        });
    }

    /**
     * Get number of polygons used in interaction with balance point layer.
     *
     * @param balanceLayerViewIds array layer view ids of balance attribute layer
     * @param baseLayerViewId layer view of territory base layer
     * @param territoryMainLayerViewId layer view of main territory layer
     * @param opts
     * 
     * @returns a dfd.promise
     */
    getUsedPolygons(balanceLayerViewIds: number[], baseLayerViewId: number, territoryMainLayerViewId: number, opts?: ApiOpts) {
        if (!ValidateModule.checkMandatoryArg(balanceLayerViewIds)) {
            return es.util.deferred.rejected();
        }

        if (!ValidateModule.checkMandatoryArg(baseLayerViewId)) {
            return es.util.deferred.rejected();
        }

        if (!ValidateModule.checkMandatoryArg(territoryMainLayerViewId)) {
            return es.util.deferred.rejected();
        }

        const url = es.api.getURL(this.resourceName) + "/used_polygons"
            + "?balanceLayerViewIds="+ es.util.url.stringify(balanceLayerViewIds)
            + "&baseLayerViewId=" + baseLayerViewId
            + "&territoryMainLayerViewId=" + territoryMainLayerViewId;

        return this.rqBroker.doGet({
            'url': url,
            'opts': opts
        });
    }

    /**
     * Get territory limits.
     * Shared to all tenants. Configurable in properties file.
     *
     * @param opts
     * 
     * @returns a dfd.promise
     */
    getTerritoryLimits(opts?: ApiOpts) {
        const url = es.api.getURL(this.resourceName) + "/optimization_limits";

        return this.rqBroker.doGet({
            'url': url,
            'opts': opts
        });
    }

    /**
     * Temporary method used to check if the territory base layer has a pbf file or a geojson file. We hope to get
     * rid of this in 10.6 as all baselayers will have pbf by then.
     * 
     * @param layerViewId
     * @param opts
     * 
     * @returns
     */
    hasPbf(layerViewId: number, opts?: ApiOpts) {
        return this.cache.doGet(this.rqBroker, this.resourceName + '_has_pbf', es.api.getURL(this.resourceName) + "/has_pbf/" + layerViewId, layerViewId, opts);
    }

    /**
     * 
     * @param meta 
     * @param opts 
     * 
     * @returns 
     */
    createCentersDataset(meta: object, opts?: ApiOpts) {
        return this.rqBroker.doPost({
            'url': es.api.getURL(this.resourceName) + "/create_territory_centers",
            'opts': opts,
            'data': meta
        });
    }

    /**
     * 
     * @param mainLayerViewId 
     * @param baseLayerMetaId 
     * @param basePointsLayerViewId 
     * @param geom 
     * @param opts 
     * 
     * @returns 
     */
    fillGaps(mainLayerViewId: number, baseLayerMetaId: number, basePointsLayerViewId: number, geom, opts?: ApiOpts) {
        const url = es.api.getURL(this.resourceName) + "/fill_gaps";

        return this.rqBroker.doPost({
            'url': url,
            'opts': opts,
            'data': {
                mainLayerViewId: mainLayerViewId,
                baseLayerMetaId: baseLayerMetaId,
                basePointsLayerViewId: basePointsLayerViewId,
                geoShape: {geom: geom}
            }
        });
    }

    /**
     * 
     * @param geoMapId 
     * @param accountLayerViewId 
     * @param opts 
     * 
     * @returns 
     */
    deriveExceptionsFromSfAccounts(geoMapId: number, accountLayerViewId: number, opts?: ApiOpts) {
        const url = es.api.getURL(this.resourceName) + "/derive_exceptions_from_sf_accounts"
            + "?geoMapId=" + geoMapId
            + "&accountLayerViewId=" + accountLayerViewId;

        return this.rqBroker.doGet({
            'url': url,
            'opts': opts
        });
    }

    /**
     * 
     * @param geoMapId 
     * @param opts 
     * 
     * @returns 
     */
    getSFExtraFieldsSummary(geoMapId: number, opts?: ApiOpts) {
        if (!ValidateModule.checkId(geoMapId)) {
            return es.util.deferred.rejected();
        }

        const url = es.api.getURL(this.resourceName) + "/sf_extra_edit_summary/" + geoMapId;

        return this.rqBroker.doGet({
            'url': url,
            'opts': opts
        });
    }
    
    /**
     * 
     * @param territoryMainLayerViewId 
     * @param opts 
     * 
     * @returns 
     */
    getLoadValues(territoryMainLayerViewId: number, opts?: ApiOpts) {
        return this.cache.doGet(this.rqBroker, this.resourceName + "load_values", es.api.getURL(this.resourceName) + "/load_values/" + territoryMainLayerViewId, territoryMainLayerViewId, opts);
    }

    /**
     * Creates territories from drive-time isochrones or radius buffers.
     * For each point/rep in the specified layer, generates a buffer and assigns intersecting base items.
     *
     * This is an async operation. Poll the returned taskId until complete, then call
     * saveAll(status.layerView.id, status.attributesList) to persist the changes.
     *
     * e.g.:
     * ```
     * const result = await es.api.territory.createFromDriveTime(meta);
     * const taskId = result.taskId;
     * // Poll status until complete...
     * const status = await es.api.status.getStatus(taskId);
     * if (status.percentage === 100 && status.attributesList) {
     *     await es.api.territory.saveAll(status.layerView.id, status.attributesList);
     * }
     * ```
     *
     * @param meta Configuration for territory creation:
     *   - territoryTempGeoMapId: ID of the territory temp geomap (required)
     *   - repsLayerViewId: Layer view containing rep/origin points (required)
     *   - driveTimeMinutes: Drive time in minutes (for isochrone mode) - mutually exclusive with radius
     *   - radiusDistance: Distance value (for radius mode) - mutually exclusive with driveTime
     *   - radiusUnit: Distance unit (for radius mode) - required if radiusDistance is set
     *   - nearestMode: true for Voronoi/nearest mode - mutually exclusive with driveTime/radius
     *   - addVoronoiLayer: true to add a visual Voronoi layer (nearest mode only)
     * @param opts
     *
     * @returns Promise resolving to {taskId: string} - poll status until attributesList is available
     */
    createFromDriveTime(meta: object, opts?: ApiOpts) {
        if (!ValidateModule.checkMandatoryArg(meta)) {
            return es.util.deferred.rejected();
        }

        const url = es.api.getURL(this.resourceName) + "/drive_time/create";

        return this.rqBroker.doPost({
            url: url,
            data: meta,
            opts: opts
        });
    }
}

/**
 * 
 * @param layerViewId 
 * 
 * @returns 
 */
function getAttrTypeMap(layerViewId: number) {
    return es.api.layerInfo.getForLayerView(layerViewId).then(function(layerInfo) {
        const map = {};
        const attrs = layerInfo.attributes;
        for (let a = attrs.length; a-- > 0;) {
            const attr = attrs[a];
            map[attr.name] = attr.attrType;
        }

        return map;
    });
}

/**
 * 
 * @param feature 
 * @param attrTypeMap 
 */
function processFeatureBeforeSave(feature, attrTypeMap) {
    delete feature.ES_GEOM_STATUS_CODE;

    for (let attrName in feature) {
        const value = feature[attrName];
        if (value != null) {
            switch (attrTypeMap[attrName]) {
                case 'TIMESTAMP': feature[attrName] = value.getTime(); break;
            }
        }
    }
}

/**
 * 
 * @param territoryFeatures 
 */
function processFeaturesOrderBeforeSave(territoryFeatures) {
    territoryFeatures.sort((f1, f2) => {
        if (f1.ES_LEVEL) {
            return (f1.ES_LEVEL - f2.ES_LEVEL);
        }

        return (f1.ES_ID - f2.ES_ID);
    });

    //validateUpperLevels(territoryFeatures, true);
}

/**
 * 
 * @param feature 
 * @param attrTypeMap 
 */
function processFeatureAfterLoad(feature, attrTypeMap) {
    for (const attrName in feature) {
        const value = feature[attrName];
        if (value != null) {
            switch (attrTypeMap[attrName]) {
                case 'TIMESTAMP': feature[attrName] = new Date(value); break;
            }
        }
    }
}
